/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
import {
	type Currencies,
	type MarketViewListEntry,
	type MarketViewSettings,
	type ReferenceUniverseListEntry,
	type StandardInstrumentDto,
	type StandardInstrumentsClassificationDto,
	type UserInstrumentClassificationDto,
	type UserInstrumentDto,
	FieldKey,
} from "@eu.mdotm.api/sphere";
import { hasAccess } from "$root/components/AuthorizationGuard";
import type { InvestableUniverseInstrument } from "$root/components/Portfolio/CreatePortfolio/constraints/const";
import type { EditPortfolioV4Props } from "$root/components/Portfolio/CreatePortfolio/EditPortfolio.shared-types";
import type { Instrument } from "$root/functional-areas/instruments-editor/const";
import { quantityProposalColumns } from "$root/functional-areas/instruments-editor/quantity/proposal-columns";
import { userClassifications$ } from "$root/functional-areas/instruments/user-classifications";
import { marketViewRepository } from "$root/functional-areas/market-view/repository";
import type { ReviewGridRow, ReviewInstrument } from "$root/functional-areas/proxies/const";
import type { QuantityReviewRow } from "$root/functional-areas/proxies/quantity-review/rows";
import { type IUser, getCurrentUser, user$ } from "$root/functional-areas/user";
import { formatDate, formatNumber, formatNumberWithBankersRounding } from "$root/localization/formatters";
import type { ReferenceItem } from "$root/pages/PortfoliosStudio/common";
import type { InvestmentItem } from "$root/pages/PortfoliosStudio/PortfolioList/const";
import { readAtom, useAtomValue } from "@mdotm/shadcn/legacy/reactivity";
import type { UseQueryResult } from "@tanstack/react-query";
import type { CellClassParams, ColDef, ColDefField } from "ag-grid-community";
import type { CustomCellRendererProps } from "ag-grid-react";
import { t } from "i18next";
import { useMemo } from "react";
import { isRowBlocked } from "../custom-column/instrument/blocking-issue";
import type { ColumnOverrideParams } from "./common-columns-definition";
import {
	type CompareInvestmentProps,
	type InstrumentActionsOverrides,
	sphereColumnsDef,
} from "./platform-columns-definition";

export const columnsDefinition = {
	useInstrumentCustomisation(opts?: { mode?: "read" | "edit"; onExport?: () => void }) {
		const mode = opts?.mode ?? "edit";
		const onExport = opts?.onExport;
		const classifications = useAtomValue(userClassifications$);
		return useMemo(
			() => this.instrumentCustomisation(classifications ?? [], mode, { onExport }),
			[classifications, mode, onExport],
		);
	},
	useInstrumentComposition(mode: "read" | "edit" = "edit") {
		const classifications = useAtomValue(userClassifications$);
		return useMemo(() => this.instrumentComposition(classifications ?? [], mode), [classifications, mode]);
	},
	useInstrumentProposalComposition(mode: "read" | "edit" = "edit") {
		const classifications = useAtomValue(userClassifications$);
		return useMemo(() => this.instrumentProposalComposition(classifications ?? [], mode), [classifications, mode]);
	},
	useInstrumentUniverseComposition(mode: "read" | "edit" = "edit") {
		const classifications = useAtomValue(userClassifications$);
		return useMemo(() => this.instrumentUniverseComposition(classifications ?? [], mode), [classifications, mode]);
	},
	useInstrumentReviewComposition(mode: "read" | "edit" = "edit") {
		const classifications = useAtomValue(userClassifications$);
		return useMemo(() => this.instrumentReviewComposition(classifications ?? [], mode), [classifications, mode]);
	},
	useInstrumentUniverseReviewComposition(mode: "read" | "edit" = "edit") {
		const classifications = useAtomValue(userClassifications$);
		return useMemo(
			() => this.instrumentUniverseReviewComposition(classifications ?? [], mode),
			[classifications, mode],
		);
	},
	useInstrumentQuantityReviewComposition(mode: "read" | "edit" = "edit", extra?: { currency?: Currencies | null }) {
		const classifications = useAtomValue(userClassifications$);
		const currency = extra?.currency;
		return useMemo(
			() => this.instrumentQuantityReviewComposition(classifications ?? [], mode, { currency }),
			[classifications, mode, currency],
		);
	},
	useInstrumentInvestableUniverse(area: EditPortfolioV4Props["area"]["name"]) {
		const classifications = useAtomValue(userClassifications$);
		return useMemo(() => this.instrumentInvestableUniverse(classifications ?? [], area), [classifications, area]);
	},
	useInstrumentStandardCustomisation() {
		const classifications = useAtomValue(userClassifications$);
		return useMemo(() => this.instrumentStandardCustomisation(classifications ?? []), [classifications]);
	},
	usePortfolioList(params?: { compare?: CompareInvestmentProps }) {
		return useMemo(
			() => this.portfolioList(params),
			[params?.compare?.onCompareInvestments, params?.compare?.queryConfig],
		);
	},
	useReferenceList() {
		return useMemo(() => this.referenceList(), []);
	},
	instrumentCustomisation(
		instrumentColumns: UserInstrumentClassificationDto[],
		mode: "read" | "edit" = "edit",
		opts?: { onExport?: () => void },
	) {
		const user = getCurrentUser();

		return [
			sphereColumnsDef.string<UserInstrumentDto>("name", {
				editable: mode === "edit",
				initialPinned: "left",
				headerName: "Name",
				colId: "NAME",
				minWidth: 200,
				cellClass: (row) => `instrument-type:${row.data?.type}`,
			}),
			sphereColumnsDef.description<UserInstrumentDto>("description.value", {
				headerName: "Description",
				colId: "DESCRIPTION",
				editable: true,
				minWidth: 150,
			}),
			sphereColumnsDef.string<UserInstrumentDto>("identifiers.ISIN", {
				headerName: "Isin",
				colId: "ISIN",
				editable: false,
				minWidth: 150,
			}),
			sphereColumnsDef.string<UserInstrumentDto>("identifiers.CUSIP", {
				headerName: "Cusip",
				colId: "CUSIP",
				editable: false,
				minWidth: 150,
			}),
			sphereColumnsDef.string<UserInstrumentDto>("identifiers.BLOOMBERG_TICKER", {
				headerName: "Bloomberg ticker",
				colId: "BLOOMBERG_TICKER",
				editable: false,
				minWidth: 150,
			}),
			sphereColumnsDef.string<UserInstrumentDto>("identifiers.ALIAS", {
				headerName: "Alias",
				colId: "ALIAS",
				editable: mode === "edit",
				minWidth: 150,
			}),
			sphereColumnsDef.integer<UserInstrumentDto>("linkedEntities", {
				headerName: "Linked entities",
				colId: "LINKED_ENTITIES",
				editable: false,
				minWidth: 150,
			}),
			mode === "edit"
				? sphereColumnsDef.instrumentCustomisationActions<UserInstrumentDto>({
						colId: "ACTION",
						headerName: "Action",
						editable: false,
						pinned: "right",
						lockPinned: true,
						lockPosition: false,
						lockVisible: true,
						resizable: false,
						hide: false,
						onExport: opts?.onExport,
					})
				: sphereColumnsDef.instrumentCompositionReadActions<UserInstrumentDto>({
						colId: "ACTION",
						headerName: "Action",
						editable: false,
						pinned: "right",
						lockPinned: true,
						lockPosition: false,
						lockVisible: true,
						resizable: false,
						hide: false,
					}),
			...(hasAccess(user, { requiredService: "RISK_MODEL_COLUMN" })
				? [
						sphereColumnsDef.riskModel<UserInstrumentDto>(
							mode === "edit" ? "instrumentCustomisation" : "instrumentCompositionRead",
						),
					]
				: []),
			...instrumentColumns
				.map((columnConfig) =>
					matchUserClassificationColumn<UserInstrumentDto>(columnConfig, {
						editable: mode === "edit",
						...(mode === "edit" ? {} : { mainMenuItems: undefined }),
						minWidth: 150,
					}),
				)
				.filter((col) => col != null),
		];
	},
	instrumentComposition(instrumentColumns: UserInstrumentClassificationDto[], mode: "read" | "edit" = "edit") {
		return [
			...instrumentIdentityColumns(),
			sphereColumnsDef.instrumentCompositionWeight({
				initialWidth: 132,
				editable: mode === "edit",
				pinned: "right",
				lockPinned: true,
				lockPosition: false,
				lockVisible: true,
				minWidth: 132,
			}),
			compositionActionColumn(mode, sphereColumnsDef.instrumentCompositionEditActions),
			// TODO: should we show it in compositions too? In that case, there is an inconsistency in
			// the DTOs, in instrument customisation the field is riskModel, in compositions it's proxies
			// sphereColumnsDef.riskModel<UserInstrumentDto>(),
			...userClassificationColumns(instrumentColumns),
		];
	},
	/**
	 * Quantity-mode (MDOT-24700) composition editor columns: the identity columns, the
	 * {@link quantityCellColumns} and the user's classifications. `extra.currency` is the AUM
	 * budget's, and reaches the market-value column.
	 */
	instrumentQuantityComposition(
		instrumentColumns: UserInstrumentClassificationDto[],
		mode: "read" | "edit" = "edit",
		extra?: { currency?: Currencies | null },
	) {
		return [
			...instrumentIdentityColumns(),
			...quantityCellColumns(mode, extra?.currency),
			compositionActionColumn(mode, sphereColumnsDef.instrumentQuantityCompositionEditActions),
			...userClassificationColumns(instrumentColumns),
		];
	},
	/**
	 * Quantity-mode shares-proposal columns: the identity columns, the proposal / previous / delta
	 * value columns of {@link quantityProposalColumns} — the previous ones labelled by
	 * `extra.proposalDate`, the comparison of positions left out by `extra.summary` on a pending
	 * conversion — then the actions and the user's classifications.
	 */
	instrumentQuantityProposalComposition(
		instrumentColumns: UserInstrumentClassificationDto[],
		mode: "read" | "edit" = "edit",
		extra?: Parameters<typeof quantityProposalColumns>[1],
	) {
		return [
			...instrumentIdentityColumns(),
			...quantityProposalColumns(mode, extra),
			compositionActionColumn(mode, sphereColumnsDef.instrumentQuantityCompositionEditActions),
			...userClassificationColumns(instrumentColumns),
		];
	},
	instrumentProposalComposition(
		instrumentColumns: UserInstrumentClassificationDto[],
		mode: "read" | "edit" = "edit",
		extra?: { proposalDate?: string },
	) {
		return [
			...instrumentIdentityColumns({ minWidth: 250 }),
			sphereColumnsDef.percentage<Instrument>("previousWeight", {
				headerName: t("TABLE.HEADERS.COMPOSITION_AT", {
					date: extra?.proposalDate ? formatDate(extra.proposalDate, { omitYear: true }) : "",
				}),
				colId: "PREVIOUS_WEIGHT",
				pinned: mode === "read" ? "right" : undefined,
				cellClassRules: {
					"opacity-50": (params) => Boolean(params.node.data?.deleted),
				},
				tooltipValueGetter: (params) =>
					typeof params.value === "number"
						? `${formatNumber(params.value, { maxDecimalPlaces: 16, minDecimalPlaces: 0 })}%`
						: null,
				valueFormatter(params) {
					const decimalPlaces = readAtom(user$).userDecimalPlacesPreferences;
					return `${formatNumberWithBankersRounding(params.value ?? 0, decimalPlaces)}%`;
				},
				aggFunc: "sum",
				minWidth: 150,
			}),
			sphereColumnsDef.percentage<Instrument>("enhancedWeight", {
				headerName: t("TABLE.HEADERS.ENHANCED_WEIGHT"),
				colId: "ENHANCED_WEIGHT",
				pinned: mode === "read" ? "right" : undefined,
				cellClassRules: {
					"opacity-50": (params) => Boolean(params.node.data?.deleted),
					"font-semibold": (params) => Boolean(params.node.group),
				},
				tooltipValueGetter: (params) =>
					typeof params.value === "number"
						? `${formatNumber(params.value, { maxDecimalPlaces: 16, minDecimalPlaces: 0 })}%`
						: null,
				valueFormatter(params) {
					const decimalPlaces = readAtom(user$).userDecimalPlacesPreferences;
					return `${formatNumberWithBankersRounding(params.value ?? 0, decimalPlaces)}%`;
				},
				aggFunc: "sum",
				minWidth: 150,
			}),
			sphereColumnsDef.instrumentDeltaWeight<Instrument>({
				headerName: t("TABLE.HEADERS.DIFFERENCE"),
				colId: "DELTA_WEIGHT",
				pinned: mode === "read" ? "right" : undefined,
				cellClassRules: {
					"opacity-50": (params) => Boolean(params.node.data?.deleted),
					"font-semibold": (params) => Boolean(params.node.group),
				},
				minWidth: 150,
			}),
			...(mode === "edit"
				? [
						sphereColumnsDef.instrumentCompositionWeight({
							initialWidth: 132,
							pinned: "right",
							editable: mode === "edit",
							lockPinned: true,
							lockPosition: false,
							lockVisible: true,
							minWidth: 132,
						}),
					]
				: []),
			mode === "edit"
				? sphereColumnsDef.instrumentCompositionEditActions<Instrument>({
						colId: "ACTION",
						headerName: "Action",
						editable: false,
						hide: false,
						pinned: "right",
						lockPinned: true,
						// lockPosition: false,
						lockVisible: true,
						resizable: false,
						cellClassRules: {
							"hidden!": (params) => {
								return Boolean(params.node.rowPinned === "bottom" && params.node.footer);
							},
						},
					})
				: sphereColumnsDef.instrumentCompositionReadActions<Instrument>({
						colId: "ACTION",
						headerName: "Action",
						editable: false,
						hide: false,
						pinned: "right",
						lockPinned: true,
						// lockPosition: false,
						lockVisible: true,
						resizable: false,
					}),
			// TODO: should we show it in compositions too? In that case, there is an inconsistency in
			// the DTOs, in instrument customisation the field is riskModel, in compositions it's proxies
			// sphereColumnsDef.riskModel<UserInstrumentDto>(),
			...userClassificationColumns(instrumentColumns),
		];
	},
	instrumentUniverseComposition(instrumentColumns: UserInstrumentClassificationDto[], mode: "read" | "edit" = "edit") {
		return [
			...instrumentIdentityColumns({ initialWidth: 250 }),
			mode === "edit"
				? sphereColumnsDef.instrumentCompositionEditActions<Instrument>({
						colId: "ACTION",
						headerName: "Action",
						editable: false,
						pinned: "right",
						lockPinned: true,
						lockPosition: false,
						lockVisible: true,
						resizable: false,
						hide: false,
						cellClassRules: {
							"opacity-50": (params) => Boolean(params.node.data?.deleted),
						},
					})
				: sphereColumnsDef.instrumentCompositionReadActions<Instrument>({
						colId: "ACTION",
						headerName: "Action",
						editable: false,
						pinned: "right",
						lockPinned: true,
						lockPosition: false,
						lockVisible: true,
						resizable: false,
						hide: false,
						cellClassRules: {
							"opacity-50": (params) => Boolean(params.node.data?.deleted),
						},
					}),
			// TODO: should we show it in compositions too? In that case, there is an inconsistency in
			// the DTOs, in instrument customisation the field is riskModel, in compositions it's proxies
			// sphereColumnsDef.riskModel<UserInstrumentDto>(),
			...userClassificationColumns(instrumentColumns),
		];
	},
	instrumentUniverseReviewComposition(
		instrumentColumns: UserInstrumentClassificationDto[],
		mode: "read" | "edit" = "edit",
	) {
		return [
			...reviewIdentityColumns(),
			reviewActionColumn(mode),
			// TODO: should we show it in compositions too? In that case, there is an inconsistency in
			// the DTOs, in instrument customisation the field is riskModel, in compositions it's proxies
			// sphereColumnsDef.riskModel<UserInstrumentDto>(),
			...reviewUserClassificationColumns(instrumentColumns),
		];
	},
	instrumentReviewComposition(instrumentColumns: UserInstrumentClassificationDto[], mode: "read" | "edit" = "edit") {
		return [
			...reviewIdentityColumns({ cellClass: (row) => `instrument-type:${row.data?.type}` }),
			reviewActionColumn(mode),
			sphereColumnsDef.percentage<ReviewInstrument>("weight", {
				headerName: "Weight",
				colId: "WEIGHT",
				initialWidth: 132,
				cellClassRules: {
					"opacity-50": isInstrumentRowDeleted,
				},
				minWidth: 132,
			}),
			// TODO: should we show it in compositions too? In that case, there is an inconsistency in
			// the DTOs, in instrument customisation the field is riskModel, in compositions it's proxies
			// sphereColumnsDef.riskModel<UserInstrumentDto>(),
			...reviewUserClassificationColumns(instrumentColumns),
		];
	},
	/**
	 * Review of a portfolio in quantities: the review columns, with the {@link quantityCellColumns} read-only
	 * and no action on the cash row. `extra.currency` is the AUM budget's.
	 */
	instrumentQuantityReviewComposition(
		instrumentColumns: UserInstrumentClassificationDto[],
		mode: "read" | "edit" = "edit",
		extra?: { currency?: Currencies | null },
	): ColDef<QuantityReviewRow>[] {
		return [
			...reviewIdentityColumns<QuantityReviewRow>(),
			quantityReviewActionColumn(mode),
			...quantityCellColumns<QuantityReviewRow>("read", extra?.currency),
			...reviewUserClassificationColumns<QuantityReviewRow>(instrumentColumns),
		];
	},
	instrumentInvestableUniverse(
		instrumentColumns: UserInstrumentClassificationDto[],
		area: EditPortfolioV4Props["area"]["name"],
	) {
		return [
			sphereColumnsDef.string<InvestableUniverseInstrument>("name", {
				editable: false,
				initialPinned: "left",
				headerName: "Name",
				colId: "NAME",
				minWidth: 200,
				cellClass: (row) => `instrument-type:${row.data?.type}`,
			}),
			...(area === "create" || area === "draft"
				? []
				: [
						sphereColumnsDef.investableUniverseStatus<InvestableUniverseInstrument>({
							colId: "STATUS",
							headerName: "Status",
						}),
					]),
			sphereColumnsDef.description<InvestableUniverseInstrument>("description.value", {
				headerName: "Description",
				colId: "DESCRIPTION",
				editable: false,
				minWidth: 150,
			}),
			...identifierColumns<InvestableUniverseInstrument>(),
			sphereColumnsDef.instrumentCustomisationActions<InvestableUniverseInstrument>({
				colId: "ACTION",
				headerName: "Action",
				editable: false,
				pinned: "right",
				lockPinned: true,
				lockPosition: false,
				lockVisible: true,
				resizable: false,
				hide: false,
			}),
			// TODO: should we show it in compositions too? In that case, there is an inconsistency in
			// the DTOs, in instrument customisation the field is riskModel, in compositions it's proxies
			// sphereColumnsDef.riskModel<UserInstrumentDto>(),
			...instrumentColumns
				.map((columnConfig) =>
					matchUserClassificationColumn<InvestableUniverseInstrument>(columnConfig, {
						editable: false,
						mainMenuItems: undefined,
						minWidth: 150,
					}),
				)
				.filter((col) => col != null),
		];
	},
	instrumentStandardCustomisation(instrumentColumns: StandardInstrumentsClassificationDto[]) {
		return [
			sphereColumnsDef.string<StandardInstrumentDto>("name", {
				headerName: "Name",
				colId: "NAME",
				minWidth: 200,
				cellClass: (row) => `instrument-type:${row.data?.type}`,
			}),
			sphereColumnsDef.string<StandardInstrumentDto>("identifier", {
				headerName: "Isin",
				colId: "ISIN",
				editable: false,
				minWidth: 200,
			}),
			// TODO: should we show it in compositions too? In that case, there is an inconsistency in
			// the DTOs, in instrument customisation the field is riskModel, in compositions it's proxies
			// sphereColumnsDef.riskModel<UserInstrumentDto>(),
			...instrumentColumns
				.map((columnConfig) =>
					matchUserClassificationColumn<StandardInstrumentDto>(columnConfig, {
						mainMenuItems: undefined,
						editable: false,
						minWidth: 150,
					}),
				)
				.filter((col) => col != null),
		];
	},
	useMarketViewList() {
		const queryMarketViewSettings = marketViewRepository.marketViewSettings.useQuery();
		return useMemo(() => columnsDefinition.marketViewList(queryMarketViewSettings), [queryMarketViewSettings]);
	},
	marketViewList(queryConfigMarketViewSettings?: UseQueryResult<MarketViewSettings>) {
		return [
			sphereColumnsDef.marketViewName<MarketViewListEntry>(),
			sphereColumnsDef.sharingWith<MarketViewListEntry>("marketView"),
			sphereColumnsDef.marketViewType<MarketViewListEntry>(queryConfigMarketViewSettings),
			sphereColumnsDef.creationDate<MarketViewListEntry>(),
			sphereColumnsDef.forecastHorizon<MarketViewListEntry>(),
			sphereColumnsDef.marketViewExpiration<MarketViewListEntry>(),
			sphereColumnsDef.colChooserAndCtxMenuTrigger<MarketViewListEntry>(),
		];
	},
	useUniverseList() {
		const user = useAtomValue(user$);
		return useMemo(() => columnsDefinition.universeList(user), [user]);
	},
	universeList(user: IUser) {
		return [
			sphereColumnsDef.universeName<ReferenceUniverseListEntry>(),
			...(!hasAccess(user, { requiredService: "GROUPS_UNIVERSE" })
				? []
				: [sphereColumnsDef.universeType<ReferenceUniverseListEntry>()]),
			sphereColumnsDef.sharingWith<ReferenceUniverseListEntry>("universe"),
			sphereColumnsDef.entityStatus<ReferenceUniverseListEntry>("status", {
				colId: "STATUS",
			}),
			sphereColumnsDef.inceptionDate<ReferenceUniverseListEntry>(),
			sphereColumnsDef.lastStatusUpdate<ReferenceUniverseListEntry>(),
			sphereColumnsDef.linkedPortfolios<ReferenceUniverseListEntry>(),
			sphereColumnsDef.colChooserAndCtxMenuTrigger<ReferenceUniverseListEntry>(),
		];
	},
	referenceList() {
		return [
			sphereColumnsDef.referenceInvestmentName<ReferenceItem>({ width: 320 }),
			sphereColumnsDef.entityStatus<ReferenceItem>("status", { colId: FieldKey.Status }),
			sphereColumnsDef.sharingWith<ReferenceItem>("targetPortfolio"),
			sphereColumnsDef.inceptionDate<ReferenceItem>(),
			sphereColumnsDef.lastStatusUpdate<ReferenceItem>(),
			sphereColumnsDef.linkedPortfolios<ReferenceItem>(),
			sphereColumnsDef.referenceInvestmentLabel<ReferenceItem>(),
			sphereColumnsDef.colChooserAndCtxMenuTrigger<ReferenceItem>(),
		];
	},
	portfolioList(params?: { compare?: CompareInvestmentProps }) {
		return [
			sphereColumnsDef.investmentName<InvestmentItem>(params?.compare, { colId: "NAME", pinned: "left" }),
			sphereColumnsDef.investmentKeyMetric<InvestmentItem>(),
			sphereColumnsDef.sharingWith<InvestmentItem>("portfolio"),
			sphereColumnsDef.date<InvestmentItem>("creationTime", {
				colId: FieldKey.InceptionDate,
				headerName: t(`TABLE.HEADERS.INCEPTION_DATE`),
			}),
			sphereColumnsDef.string<InvestmentItem>("action", {
				colId: FieldKey.CurrentAction,
				headerName: t(`TABLE.HEADERS.CURRENT_ACTION`),
				valueGetter(row) {
					const action = row.data?.action;
					return action ? t(`PORTFOLIO_ACTION_STATUS.${action}`) : "";
				},
			}),
			sphereColumnsDef.entityStatus<InvestmentItem>("status", { colId: FieldKey.Status }),
			sphereColumnsDef.date<InvestmentItem>("modificationTime", {
				colId: FieldKey.LastStatusUpdate,
				headerName: t(`TABLE.HEADERS.LAST_STATUS_UPDATE`),
			}),
			sphereColumnsDef.investmentAutoSync<InvestmentItem>({
				colId: FieldKey.AutoSync,
			}),
			sphereColumnsDef.investmentMarketView<InvestmentItem>({
				colId: FieldKey.MarketViewName,
			}),
			sphereColumnsDef.string<InvestmentItem>("currencyName", {
				headerName: t(`TABLE.HEADERS.CURRENCY_NAME`),
				colId: FieldKey.CurrencyName,
			}),
			sphereColumnsDef.string<InvestmentItem>("benchmarkName", {
				headerName: t(`TABLE.HEADERS.BENCHMARK_NAME`),
				colId: FieldKey.BenchmarkName,
			}),
			sphereColumnsDef.string<InvestmentItem>("universeName", {
				headerName: t(`TABLE.HEADERS.UNIVERSE_NAME`),
				colId: FieldKey.UniverseName,
			}),
			sphereColumnsDef.string<InvestmentItem>("commentaryTemplate", {
				headerName: t(`TABLE.HEADERS.COMMENTARY_TEMPLATE`),
				colId: FieldKey.CommentaryTemplate,
			}),
			sphereColumnsDef.floating<InvestmentItem>("benchmarkEfficiencyRatio1M", {
				headerName: t(`TABLE.HEADERS.BENCHMARK_EFFICIENCY_RATIO_1M`),
				colId: FieldKey.BenchmarkEfficiencyRatio1M,
			}),
			sphereColumnsDef.percentage<InvestmentItem>("benchmarkMaxDrawdown1M", {
				headerName: t(`TABLE.HEADERS.BENCHMARK_MAX_DRAWDOWN_1M`),
				colId: FieldKey.BenchmarkMaxDrawdown1M,
			}),
			sphereColumnsDef.percentage<InvestmentItem>("benchmarkPerformance1M", {
				headerName: t(`TABLE.HEADERS.BENCHMARK_PERFORMANCE_1M`),
				colId: FieldKey.BenchmarkPerformance1M,
			}),
			sphereColumnsDef.floating<InvestmentItem>("benchmarkSortino1M", {
				headerName: t(`TABLE.HEADERS.BENCHMARK_SORTINO_1M`),
				colId: FieldKey.BenchmarkSortino1M,
			}),
			sphereColumnsDef.percentage<InvestmentItem>("benchmarkVolatility1M", {
				headerName: t(`TABLE.HEADERS.BENCHMARK_VOLATILITY_1M`),
				colId: FieldKey.BenchmarkVolatility1M,
			}),
			sphereColumnsDef.floating<InvestmentItem>("efficiencyRatio1M", {
				headerName: t(`TABLE.HEADERS.EFFICIENCY_RATIO_1M`),
				colId: FieldKey.EfficiencyRatio1M,
			}),
			sphereColumnsDef.percentage<InvestmentItem>("maxDrawdown1M", {
				headerName: t(`TABLE.HEADERS.MAX_DRAWDOWN_1M`),
				colId: FieldKey.MaxDrawdown1M,
			}),
			sphereColumnsDef.percentage<InvestmentItem>("performance1M", {
				headerName: t(`TABLE.HEADERS.PERFORMANCE_1M`),
				colId: FieldKey.Performance1M,
			}),
			sphereColumnsDef.floating<InvestmentItem>("sortino1M", {
				headerName: t(`TABLE.HEADERS.SORTINO_1M`),
				colId: FieldKey.Sortino1M,
			}),
			sphereColumnsDef.percentage<InvestmentItem>("volatility1M", {
				headerName: t(`TABLE.HEADERS.VOLATILITY_1M`),
				colId: FieldKey.Volatility1M,
			}),
			sphereColumnsDef.investmentType<InvestmentItem>({
				colId: FieldKey.LiveTrading,
			}),
			sphereColumnsDef.investmentWeightingMethod<InvestmentItem>({
				colId: FieldKey.FixedWeights,
			}),
			sphereColumnsDef.colChooserAndCtxMenuTrigger<InvestmentItem>(),
		];
	},
};

/**
 * The four identifier columns every instrument grid carries: ISIN, CUSIP, Bloomberg ticker and
 * alias. Read-only and equally wide in every grid; `opt` is merged into each of them.
 */
function identifierColumns<T extends { identifiers?: unknown }>(opt?: ColumnOverrideParams<T, string | undefined>) {
	return (
		[
			["identifiers.ISIN", "Isin", "ISIN"],
			["identifiers.CUSIP", "Cusip", "CUSIP"],
			["identifiers.BLOOMBERG_TICKER", "Bloomberg ticker", "BLOOMBERG_TICKER"],
			["identifiers.ALIAS", "Alias", "ALIAS"],
		] as const
	).map(([field, headerName, colId]) =>
		sphereColumnsDef.string<T>(field as ColDefField<T, string | undefined>, {
			headerName,
			colId,
			editable: false,
			minWidth: 150,
			...opt,
		}),
	);
}

/**
 * The columns a composition grid opens with: name, description and the four identifier codes.
 * Read-only in every mode — they carry the instrument's identity, not the composition's numbers.
 * `description` widens that one column where a grid gives it more room.
 */
function instrumentIdentityColumns(description?: { minWidth?: number; initialWidth?: number }) {
	return [
		sphereColumnsDef.instrumentName<Instrument>({
			headerName: "Name",
			colId: "NAME",
			minWidth: 200,
			editable: false,
			initialPinned: "left",
			cellClassRules: {
				"opacity-50": (params) => Boolean(params.node.data?.deleted),
			},
			cellClass: (row) => `instrument-type:${row.data?.type}`,
		}),
		sphereColumnsDef.description<Instrument>("description.value", {
			headerName: "Description",
			colId: "DESCRIPTION",
			editable: false,
			cellClassRules: {
				"opacity-50": (params) => Boolean(params.node.data?.deleted),
			},
			minWidth: 150,
			...description,
		}),
		...identifierColumns<Instrument>({
			cellClassRules: {
				"opacity-50": (params) => Boolean(params.node.data?.deleted),
			},
		}),
	];
}

/**
 * The classification columns the user has configured — tags, qualities and scores — which close
 * a composition grid. A field type that matches no column yields `undefined` and is dropped.
 */
function userClassificationColumns(instrumentColumns: UserInstrumentClassificationDto[]) {
	return instrumentColumns
		.map((columnConfig) =>
			matchUserClassificationColumn<Instrument>(columnConfig, {
				mainMenuItems: undefined,
				cellClassRules: {
					"opacity-50": (params) => Boolean(params.node.data?.deleted),
				},
				minWidth: 150,
			}),
		)
		.filter((col) => col != null);
}

/**
 * The quantity cells of a quantity-mode (MDOT-24700) grid: Quantity, Market price, Market value
 * and Weight. All four are RENDER-ONLY reads of row fields — values are backend-provided or
 * derived/stored by the quantity editor's edit engine, never computed in the columns.
 * `currency` is the AUM budget's, shown as a symbol in the market-value header and grand total;
 * omit it where columns are built for column-state syncing rather than rendering.
 */
function quantityCellColumns<T extends Instrument = Instrument>(mode: "read" | "edit", currency?: Currencies | null) {
	return [
		sphereColumnsDef.instrumentQuantity<T>({
			editable: mode === "edit",
			pinned: "right",
			lockPinned: true,
			lockPosition: false,
			lockVisible: true,
		}),
		sphereColumnsDef.instrumentMarketPrice<T>({
			pinned: "right",
			lockPinned: true,
			lockPosition: false,
			lockVisible: true,
		}),
		sphereColumnsDef.instrumentMarketValue<T>(
			{ currency },
			{
				editable: mode === "edit",
				pinned: "right",
				lockPinned: true,
				lockPosition: false,
				lockVisible: true,
			},
		),
		// Declared read-only: in edit mode `withQuantityCommitHandlers` enables weight edits,
		// which convert to whole-share market values through the quantity edit engine.
		sphereColumnsDef.instrumentCompositionWeight<T>({
			initialWidth: 132,
			editable: false,
			pinned: "right",
			lockPinned: true,
			lockPosition: false,
			lockVisible: true,
			minWidth: 132,
		}),
	];
}

/**
 * The action column of a composition grid: `editActions` while editing, the read-only actions
 * otherwise. The edit half is passed in — a plain composition and a quantity-mode one offer
 * different row actions.
 */
function compositionActionColumn(
	mode: "read" | "edit",
	editActions: (opt?: InstrumentActionsOverrides<Instrument>) => ColDef<Instrument>,
) {
	return mode === "edit"
		? editActions({
				colId: "ACTION",
				headerName: "Action",
				editable: false,
				pinned: "right",
				lockPinned: true,
				lockPosition: false,
				lockVisible: true,
				hide: false,
				resizable: false,
				cellClassRules: {
					"hidden!": (params) => {
						return Boolean(params.node.rowPinned === "bottom" && params.node.footer);
					},
				},
			})
		: sphereColumnsDef.instrumentCompositionReadActions<Instrument>({
				colId: "ACTION",
				headerName: "Action",
				editable: false,
				pinned: "right",
				lockPinned: true,
				lockPosition: false,
				lockVisible: true,
				hide: false,
				resizable: false,
			});
}

/**
 * A string field every `ReviewGridRow` carries, named on a row type that extends it: `ColDefField` cannot
 * resolve a path on a generic row.
 */
function reviewRowField<T extends ReviewGridRow>(
	field: ColDefField<ReviewGridRow, string | undefined>,
): ColDefField<T, string | undefined> {
	return field as ColDefField<T, string | undefined>;
}

/**
 * The columns a review grid opens with: name, review status, description and the four identifier
 * codes. `name` carries the extra name-column options a grid adds to them.
 */
function reviewIdentityColumns<T extends ReviewGridRow = ReviewInstrument>(
	name?: Pick<ColumnOverrideParams<T>, "cellClass">,
) {
	return [
		sphereColumnsDef.instrumentName<T>({
			headerName: "Name",
			colId: "NAME",
			minWidth: 200,
			editable: false,
			initialPinned: "left",
			cellClassRules: {
				"opacity-50": isInstrumentRowDeleted,
			},
			...name,
		}),
		sphereColumnsDef.instrumentReviewStatus<T>({
			pinned: "left",
			initialWidth: 150,
			cellClassRules: {
				"opacity-50": isInstrumentRowDeleted,
			},
		}),
		sphereColumnsDef.description<T>(reviewRowField("description.value"), {
			headerName: "Description",
			colId: "DESCRIPTION",
			minWidth: 150,
			editable: false,
			cellClassRules: {
				"opacity-50": isInstrumentRowDeleted,
			},
			initialWidth: 250,
		}),
		...identifierColumns<T>({
			cellClassRules: {
				"opacity-50": isInstrumentRowDeleted,
			},
		}),
	];
}

/**
 * The action column of a review grid: the review actions, rendered as nothing outside edit mode.
 */
function reviewActionColumn<T extends ReviewGridRow = ReviewInstrument>(mode: "read" | "edit") {
	return sphereColumnsDef.instrumentReviewActions<T>({
		headerName: "Action",
		colId: "ACTION",
		pinned: "right",
		lockPinned: true,
		lockPosition: false,
		lockVisible: true,
		resizable: false,
		hide: false,
		...(mode === "edit" ? {} : { cellRenderer: () => "" }),
	});
}

/** {@link reviewActionColumn} with nothing on the cash row, which is never deleted, nor on the grand total row. */
function quantityReviewActionColumn(mode: "read" | "edit"): ColDef<QuantityReviewRow> {
	const column = reviewActionColumn<QuantityReviewRow>(mode);
	return {
		...column,
		cellRenderer: (params: CustomCellRendererProps<QuantityReviewRow>) =>
			params.data == null || params.data.isCash ? "" : column.cellRenderer(params),
		onCellClicked(params) {
			if (params.data == null || params.data.isCash) {
				return;
			}
			column.onCellClicked?.(params);
		},
	};
}

/**
 * {@link userClassificationColumns} for the review grids, whose rows dim on a different rule.
 */
function reviewUserClassificationColumns<T extends ReviewGridRow = ReviewInstrument>(
	instrumentColumns: UserInstrumentClassificationDto[],
) {
	return instrumentColumns
		.map((columnConfig) =>
			matchUserClassificationColumn<T>(columnConfig, {
				mainMenuItems: undefined,
				cellClassRules: {
					"opacity-50": isInstrumentRowDeleted,
				},
				minWidth: 150,
			}),
		)
		.filter((col) => col != null);
}

function isInstrumentRowDeleted<T extends ReviewGridRow>(params: CellClassParams<T, any, any>) {
	if (params.data && isRowBlocked(params.data)) {
		return false;
	}
	return Boolean(params.node.data?.deleted);
}

function matchUserClassificationColumn<T extends UserInstrumentDto>(
	column: UserInstrumentClassificationDto,
	opt?: Omit<
		ColumnOverrideParams<T>,
		"field" | "tooltipComponent" | "cellRenderer" | "cellEditor" | "tooltipValueGetter" | "valueGetter" | "valueSetter"
	>,
) {
	switch (column.fieldType) {
		case "TAG":
			return sphereColumnsDef.tagWeight<T>(column, opt);
		case "QUALITY":
			return sphereColumnsDef.quality<T>(column, opt);
		case "SCORE":
			return sphereColumnsDef.score<T>(column, opt);
		default:
			return undefined;
	}
}
